import { cn } from "@/lib/utils";
import {
  BotIcon,
  BoxSelect,
  Component,
  Copy,
  FileCode2,
  FileMinus,
  FilePen,
  FilePlus,
  FolderMinus,
  FolderPlus,
  Frame,
  Hexagon,
  Loader2,
  LucideIcon,
  PackageCheck,
  PackageMinus,
  PackagePlus,
  Palette,
  RefreshCcwIcon,
  Save,
  SaveIcon,
  SendToBack,
  Sparkles,
  Trash,
  WholeWord,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import useActivityStore from "@/store/zustand/activityStore";
import { useDispatch, useSelector } from "react-redux";
import { useCanvasId } from "@/utils/utils";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import { useSession } from "@clerk/clerk-react";
import { useIsChangePending } from "@/store/zustand/designCommandManagerStore";

interface Props {
  timestamp: string;
  changeUndone: boolean;
  changeType: ActivityChangeType;
  selected: boolean;
  onSelect: () => void;
  isCurrentActivity: boolean;
  activityId: string;
}

// Must match in tempo-web
export enum ActivityChangeType {
  SAVE_FILE = "SAVE_FILE",
  WRITE_DEV_TOOLS = "WRITE_DEV_TOOLS",
  ADD_COMPONENT = "ADD_COMPONENT",
  CHANGE_STYLING_REACT = "CHANGE_STYLING_REACT",
  CHANGE_STYLING_CSS = "CHANGE_STYLING_CSS",
  ADD_CLASS = "ADD_CLASS",
  REMOVE_CLASS = "REMOVE_CLASS",
  WRAP_IN_DIV = "WRAP_IN_DIV",
  DUPLICATE = "DUPLICATE",
  CREATE_CSS_RULE = "CREATE_CSS_RULE",
  DELETE_CSS_RULE = "DELETE_CSS_RULE",
  ADD_JSX_ELEMENT = "ADD_JSX_ELEMENT",
  MOVE_JSX_ELEMENT = "MOVE_JSX_ELEMENT",
  REMOVE_JSX_ELEMENT = "REMOVE_JSX_ELEMENT",
  CHANGE_PROP = "CHANGE_PROP",
  CHANGE_ELEMENT_TAG = "CHANGE_ELEMENT_TAG",
  CREATE_UNSAVED_COMPONENT = "CREATE_UNSAVED_COMPONENT",
  DUPLICATE_UNSAVED_COMPONENT = "DUPLICATE_UNSAVED_COMPONENT",
  DELETE_UNSAVED_COMPONENT = "DELETE_UNSAVED_COMPONENT",
  SAVE_UNSAVED_COMPONENT = "SAVE_UNSAVED_COMPONENT",
  SAVE_UNSAVED_STORY = "SAVE_UNSAVED_STORY",
  AI_CHANGE = "AI_CHANGE",
  AI_VISUAL_COPILOT_CHANGE = "AI_VISUAL_COPILOT_CHANGE",
  AI_GENERATE_COMPONENT = "AI_GENERATE_COMPONENT",
  AI_EDIT_COMPONENT = "AI_EDIT_COMPONENT",
  CREATE_FILE = "CREATE_FILE",
  CREATE_FOLDER = "CREATE_FOLDER",
  REMOVE_FILE = "REMOVE_FILE",
  REMOVE_FOLDER = "REMOVE_FOLDER",
  RENAME_FILE = "RENAME_FILE",
  EDIT_TEXT = "EDIT_TEXT",
  AI_GENERATE_STORIES = "AI_GENERATE_STORIES",
  SAVE_ENG_SPEC = "SAVE_ENG_SPEC",
  AI_CREATE_WIREFRAME = "AI_CREATE_WIREFRAME",
  GIT_COMMIT_HOOK = "GIT_COMMIT_HOOK",
}

export const ActivityChangeTypeToName: { [key in ActivityChangeType]: string } =
  {
    [ActivityChangeType.SAVE_FILE]: "Save File",
    [ActivityChangeType.WRITE_DEV_TOOLS]: "Update Tempo Devtools",
    [ActivityChangeType.ADD_COMPONENT]: "Add Marketplace Component",
    [ActivityChangeType.CHANGE_STYLING_REACT]: "Style Change in React Element",
    [ActivityChangeType.CHANGE_STYLING_CSS]: "Style Change in CSS Rule",
    [ActivityChangeType.ADD_CLASS]: "Add Class to Element",
    [ActivityChangeType.REMOVE_CLASS]: "Remove Class from Element",
    [ActivityChangeType.WRAP_IN_DIV]: "Wrap in Div",
    [ActivityChangeType.DUPLICATE]: "Duplicate",
    [ActivityChangeType.CREATE_CSS_RULE]: "Create CSS Rule",
    [ActivityChangeType.DELETE_CSS_RULE]: "Delete CSS Rule",
    [ActivityChangeType.ADD_JSX_ELEMENT]: "Add Element",
    [ActivityChangeType.MOVE_JSX_ELEMENT]: "Move Element",
    [ActivityChangeType.REMOVE_JSX_ELEMENT]: "Delete Element",
    [ActivityChangeType.CHANGE_PROP]: "Change Prop",
    [ActivityChangeType.CHANGE_ELEMENT_TAG]: "Change Element Tag",
    [ActivityChangeType.CREATE_UNSAVED_COMPONENT]: "Create New Storyboard",
    [ActivityChangeType.DUPLICATE_UNSAVED_COMPONENT]: "Duplicate Storyboard",
    [ActivityChangeType.DELETE_UNSAVED_COMPONENT]: "Delete Storyboard",
    [ActivityChangeType.SAVE_UNSAVED_COMPONENT]: "Save Storyboard as Component",
    [ActivityChangeType.SAVE_UNSAVED_STORY]: "Save Storyboard as Story",
    [ActivityChangeType.AI_CHANGE]: "AI Edit File",
    [ActivityChangeType.AI_VISUAL_COPILOT_CHANGE]: "Eng Spec Change",
    [ActivityChangeType.AI_GENERATE_COMPONENT]: "AI Generate Component",
    [ActivityChangeType.AI_EDIT_COMPONENT]: "AI Edit Component",
    [ActivityChangeType.CREATE_FILE]: "Create File",
    [ActivityChangeType.CREATE_FOLDER]: "Create Folder",
    [ActivityChangeType.REMOVE_FILE]: "Remove File",
    [ActivityChangeType.REMOVE_FOLDER]: "Remove Folder",
    [ActivityChangeType.EDIT_TEXT]: "Edit Text",
    [ActivityChangeType.RENAME_FILE]: "Rename File",
    [ActivityChangeType.AI_GENERATE_STORIES]: "AI Generate Stories",
    [ActivityChangeType.SAVE_ENG_SPEC]: "Save From Eng Plan",
    [ActivityChangeType.AI_CREATE_WIREFRAME]: "AI Create Wireframe",
    [ActivityChangeType.GIT_COMMIT_HOOK]: "Changes from Git Commit Hook",
  };

export const ActivityChangeTypeToIcon: Partial<
  Record<ActivityChangeType, LucideIcon>
> = {
  [ActivityChangeType.SAVE_FILE]: SaveIcon,
  [ActivityChangeType.WRITE_DEV_TOOLS]: PackageCheck,
  [ActivityChangeType.ADD_COMPONENT]: Component,
  [ActivityChangeType.CHANGE_STYLING_REACT]: Palette,
  [ActivityChangeType.CHANGE_STYLING_CSS]: Palette,
  [ActivityChangeType.ADD_CLASS]: Palette,
  [ActivityChangeType.REMOVE_CLASS]: Palette,
  [ActivityChangeType.WRAP_IN_DIV]: BoxSelect,
  [ActivityChangeType.DUPLICATE]: Copy,
  [ActivityChangeType.ADD_JSX_ELEMENT]: PackagePlus,
  [ActivityChangeType.MOVE_JSX_ELEMENT]: SendToBack,
  [ActivityChangeType.REMOVE_JSX_ELEMENT]: PackageMinus,
  [ActivityChangeType.CHANGE_ELEMENT_TAG]: FileCode2,
  [ActivityChangeType.CREATE_UNSAVED_COMPONENT]: Frame,
  [ActivityChangeType.DUPLICATE_UNSAVED_COMPONENT]: Copy,
  [ActivityChangeType.DELETE_UNSAVED_COMPONENT]: Trash,
  [ActivityChangeType.SAVE_UNSAVED_COMPONENT]: Save,
  [ActivityChangeType.SAVE_UNSAVED_STORY]: Save,
  [ActivityChangeType.AI_CHANGE]: Sparkles,
  [ActivityChangeType.AI_VISUAL_COPILOT_CHANGE]: Sparkles,
  [ActivityChangeType.AI_GENERATE_COMPONENT]: Sparkles,
  [ActivityChangeType.AI_EDIT_COMPONENT]: Sparkles,
  [ActivityChangeType.CREATE_FILE]: FilePlus,
  [ActivityChangeType.CREATE_FOLDER]: FolderPlus,
  [ActivityChangeType.REMOVE_FILE]: FileMinus,
  [ActivityChangeType.REMOVE_FOLDER]: FolderMinus,
  [ActivityChangeType.EDIT_TEXT]: WholeWord,
  [ActivityChangeType.CHANGE_PROP]: Component,
  [ActivityChangeType.RENAME_FILE]: FilePen,
  [ActivityChangeType.AI_GENERATE_STORIES]: Sparkles,
  [ActivityChangeType.SAVE_ENG_SPEC]: BotIcon,
  [ActivityChangeType.AI_CREATE_WIREFRAME]: Sparkles,
  [ActivityChangeType.GIT_COMMIT_HOOK]: PackageCheck,
};

const ActivityIcon: React.FC<{ changeType: ActivityChangeType }> = ({
  changeType,
}) => {
  const defaultIcon: LucideIcon = Hexagon;
  const Icon = ActivityChangeTypeToIcon[changeType] || defaultIcon;
  return <Icon className="w-4 h-4" />;
};

const ActivityStreamItem: React.FC<Props> = (props) => {
  const canvasId = useCanvasId();

  const dispatch = useDispatch();

  const [hovering, setHovering] = useState(false);

  const activityStreamList = useActivityStore(
    (state) => state.activityStreamList
  );

  const triggerRefetchActivityStream = useActivityStore(
    (state) => state.triggerRefetchActivityStream
  );

  const { restoreActivity, restoringActivityId } = useActivityStore();

  const isChangePending = useIsChangePending();

  const { session } = useSession();

  const formattedDate = useMemo(() => {
    const date = new Date(props.timestamp);
    // Correctly specify the options with specific string literals
    const optionsDate: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
    };
    const optionsTime: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    const formattedDate = `${date.toLocaleDateString(
      "en-US",
      optionsDate
    )}, ${date.toLocaleTimeString("en-US", optionsTime)}`;

    return formattedDate;
  }, [props.timestamp]);

  return (
    <div
      className={cn(
        "flex flex-row justify-start items-center pl-4 pr-2 h-14 w-full cursor-pointer",
        props.selected
          ? "bg-primary-0 hover:bg-primary-100"
          : props.changeUndone
          ? "bg-secondary-200 hover:bg-secondary-300"
          : "bg-secondary-0 hover:bg-secondary-100"
      )}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={async () => {
        props.onSelect();
      }}
    >
      <div
        className={cn(
          "flex flex-col text-left min-w-0 rounded-none pl-0 justify-center items-start space-y-1 grow shrink"
        )}
      >
        <div className="flex flex-row justify-center items-center space-x-1 overflow-hidden min-w-0 w-full">
          <ActivityIcon changeType={props.changeType} />
          <div className="font-inter whitespace-nowrap truncate text-left normal-case text-xs font-medium grow shrink block">
            {ActivityChangeTypeToName[props.changeType]}{" "}
          </div>
        </div>
        <div
          className={cn(
            "font-inter text-left w-full normal-case text-xs",
            props.selected ? "text-white" : "text-muted-foreground"
          )}
        >
          {formattedDate}
        </div>
      </div>
      <div className="shrink-0">
        <div
          className={cn(
            "text-2xs rounded-xl px-2.5 py-0.5 bg-background-tertiary",
            !props.isCurrentActivity && !props.changeUndone && "hidden",
            props.changeUndone && "bg-destructive",
            hovering && !props.isCurrentActivity && "hidden",
            restoringActivityId === props.activityId && "hidden"
          )}
        >
          {props.isCurrentActivity ? "Current" : "Reverted"}
        </div>
        <Button
          variant="outline"
          size="xs"
          className={cn(
            "py-1 px-1.5 gap-1 text-2x rounded-md",
            !(hovering || restoringActivityId === props.activityId) && "hidden",
            props.isCurrentActivity && "hidden",
            restoringActivityId &&
              restoringActivityId !== props.activityId &&
              "hidden",
            isChangePending && "hidden"
          )}
          onClick={(e) => {
            if (!session) return;

            e.stopPropagation();
            e.preventDefault();
            restoreActivity(
              dispatch,
              canvasId,
              props.activityId,
              activityStreamList,
              session
            );
          }}
        >
          {restoringActivityId === props.activityId ? (
            <div className={cn("animate-spin")}>
              <Loader2 className="w-3 h-3" />
            </div>
          ) : (
            <RefreshCcwIcon className="w-3 h-3" />
          )}
          <span>Restore</span>
        </Button>
      </div>
    </div>
  );
};

export default ActivityStreamItem;
